import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  EmailComposerDraft,
  EmailMailboxId,
  IEmailMessage,
} from '@dataTypes/email.type'
import {
  draftToApiPayload,
  EmailFolder,
  EmailLabel,
  EmailSettingsData,
  EmailStarTag,
  getEmailMessagesErrorMessage,
  getEmailMutationErrorMessage,
  getSendEmailErrorMessage,
  isEmailBackendEnabled,
  mailboxToApiPatch,
  requestBulkDeleteEmailMessages,
  requestBulkUpdateEmailMessages,
  requestClearEmailFolder,
  requestCreateEmailFolder,
  requestCreateEmailLabel,
  requestDeleteEmailFolder,
  requestDeleteEmailLabel,
  requestEmailFolders,
  requestEmailLabels,
  requestEmailStarTags,
  requestEmailMessages,
  requestEmailSettings,
  requestSyncGoogleWorkspaceInbox,
  requestSendEmail,
  requestSetEmailLabels,
  requestUpdateEmailFolder,
  requestUpdateEmailLabel,
  requestUpdateEmailMessage,
  requestUpsertEmailDraft,
  SendEmailRequest,
  SendEmailResult,
} from '@services/api/email.api'

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const isPersistedEmailId = (id: string): boolean => UUID_REGEX.test(id)

export const filterPersistedEmailIds = (ids: string[]): string[] =>
  ids.filter(isPersistedEmailId)

export type FetchEmailMessagesArg = { silent?: boolean } | void

export const fetchEmailMessages = createAsyncThunk<
  IEmailMessage[],
  FetchEmailMessagesArg,
  { rejectValue: string }
>('email/fetchMessages', async (_arg, { rejectWithValue }) => {
  try {
    return await requestEmailMessages()
  } catch (error: unknown) {
    return rejectWithValue(getEmailMessagesErrorMessage(error))
  }
})

export const fetchEmailSettings = createAsyncThunk<
  EmailSettingsData | null,
  void,
  { rejectValue: string }
>('email/fetchSettings', async (_, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled()) return null
    return await requestEmailSettings()
  } catch (error: unknown) {
    return rejectWithValue(getEmailMessagesErrorMessage(error))
  }
})

export const fetchEmailLabels = createAsyncThunk<
  EmailLabel[],
  void,
  { rejectValue: string }
>('email/fetchLabels', async (_, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled()) return []
    return await requestEmailLabels()
  } catch (error: unknown) {
    return rejectWithValue(getEmailMessagesErrorMessage(error))
  }
})

export const fetchEmailStarTags = createAsyncThunk<
  EmailStarTag[],
  void,
  { rejectValue: string }
>('email/fetchStarTags', async (_, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled()) return []
    return await requestEmailStarTags()
  } catch (error: unknown) {
    return rejectWithValue(getEmailMessagesErrorMessage(error))
  }
})

export const syncGoogleInbox = createAsyncThunk<
  { synced: number },
  void,
  { rejectValue: string }
>('email/syncGoogleInbox', async (_, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled()) return { synced: 0 }
    const { synced } = await requestSyncGoogleWorkspaceInbox()
    return { synced }
  } catch (error: unknown) {
    return rejectWithValue(getEmailMessagesErrorMessage(error))
  }
})

export const sendEmailMessage = createAsyncThunk<
  SendEmailResult,
  SendEmailRequest,
  { rejectValue: string }
>('email/sendMessage', async (payload, { rejectWithValue }) => {
  try {
    return await requestSendEmail(payload)
  } catch (error: unknown) {
    return rejectWithValue(getSendEmailErrorMessage(error))
  }
})

export const updateEmailMessage = createAsyncThunk<
  IEmailMessage,
  { id: string; patch: Record<string, unknown> },
  { rejectValue: string }
>('email/updateMessage', async ({ id, patch }, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled() || !isPersistedEmailId(id)) {
      throw new Error('Email backend is not available')
    }
    return await requestUpdateEmailMessage(id, patch)
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const bulkUpdateEmailMessages = createAsyncThunk<
  IEmailMessage[],
  { ids: string[]; patch: Record<string, unknown> },
  { rejectValue: string }
>('email/bulkUpdateMessages', async ({ ids, patch }, { rejectWithValue }) => {
  try {
    const persistedIds = filterPersistedEmailIds(ids)
    if (!isEmailBackendEnabled() || persistedIds.length === 0) {
      return []
    }
    return await requestBulkUpdateEmailMessages(persistedIds, patch)
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const bulkDeleteEmailMessages = createAsyncThunk<
  void,
  { ids: string[] },
  { rejectValue: string }
>('email/bulkDeleteMessages', async ({ ids }, { rejectWithValue }) => {
  try {
    const persistedIds = filterPersistedEmailIds(ids)
    if (!isEmailBackendEnabled() || persistedIds.length === 0) return
    await requestBulkDeleteEmailMessages(persistedIds)
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const setEmailLabelsOnMessages = createAsyncThunk<
  void,
  { ids: string[]; labelId: string; setChecked: boolean },
  { rejectValue: string }
>(
  'email/setLabelsOnMessages',
  async ({ ids, labelId, setChecked }, { rejectWithValue }) => {
    try {
      const persistedIds = filterPersistedEmailIds(ids)
      if (!isEmailBackendEnabled() || persistedIds.length === 0) return
      await requestSetEmailLabels(persistedIds, labelId, setChecked)
    } catch (error: unknown) {
      return rejectWithValue(getEmailMutationErrorMessage(error))
    }
  },
)

export const clearEmailFolderAssignments = createAsyncThunk<
  void,
  { folderId: string },
  { rejectValue: string }
>('email/clearFolderAssignments', async ({ folderId }, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled()) return
    await requestClearEmailFolder(folderId)
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const upsertEmailDraft = createAsyncThunk<
  IEmailMessage,
  EmailComposerDraft,
  { rejectValue: string }
>('email/upsertDraft', async (draft, { rejectWithValue }) => {
  try {
    return await requestUpsertEmailDraft(draftToApiPayload(draft))
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const createEmailLabel = createAsyncThunk<
  EmailLabel,
  { name: string; color: string },
  { rejectValue: string }
>('email/createLabel', async (payload, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled()) {
      return { id: `l-${Date.now()}`, ...payload }
    }
    return await requestCreateEmailLabel(payload)
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const updateEmailLabelRemote = createAsyncThunk<
  EmailLabel,
  { id: string; name: string; color: string },
  { rejectValue: string }
>('email/updateLabel', async ({ id, name, color }, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled() || !isPersistedEmailId(id)) {
      throw new Error('Email backend is not available')
    }
    return await requestUpdateEmailLabel(id, { name, color })
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const deleteEmailLabelRemote = createAsyncThunk<
  string,
  { id: string },
  { rejectValue: string }
>('email/deleteLabel', async ({ id }, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled() || !isPersistedEmailId(id)) {
      return id
    }
    await requestDeleteEmailLabel(id)
    return id
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const fetchEmailFolders = createAsyncThunk<
  EmailFolder[],
  void,
  { rejectValue: string }
>('email/fetchFolders', async (_, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled()) return []
    return await requestEmailFolders()
  } catch (error: unknown) {
    return rejectWithValue(getEmailMessagesErrorMessage(error))
  }
})

export const createEmailFolder = createAsyncThunk<
  EmailFolder,
  { name: string },
  { rejectValue: string }
>('email/createFolder', async (payload, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled()) {
      return { id: `f-${Date.now()}`, ...payload }
    }
    return await requestCreateEmailFolder(payload)
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const updateEmailFolderRemote = createAsyncThunk<
  EmailFolder,
  { id: string; name: string },
  { rejectValue: string }
>('email/updateFolder', async ({ id, name }, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled() || !isPersistedEmailId(id)) {
      throw new Error('Email backend is not available')
    }
    return await requestUpdateEmailFolder(id, { name })
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const deleteEmailFolderRemote = createAsyncThunk<
  string,
  { id: string },
  { rejectValue: string }
>('email/deleteFolder', async ({ id }, { rejectWithValue }) => {
  try {
    if (!isEmailBackendEnabled() || !isPersistedEmailId(id)) {
      return id
    }
    await requestDeleteEmailFolder(id)
    return id
  } catch (error: unknown) {
    return rejectWithValue(getEmailMutationErrorMessage(error))
  }
})

export const buildMailboxPatch = (
  mailbox: EmailMailboxId,
): Record<string, unknown> => mailboxToApiPatch(mailbox)

export const buildFolderPatch = (
  folderId: string,
): Record<string, unknown> => ({
  folder_id: folderId,
})
